import {useAuth} from '@/components/AuthProvider';
import {RouteDropdown} from '@/components/eventRoutes/routeSelector/routeDropdown';
import {Button} from '@/components/ui/button';
import {Typography} from '@/components/ui/typography';
import {useAnalytics} from '@/hooks/useAnalytics';
import {
  useEventRegistrationActor,
  useEventRegistrationState,
} from '@/machines/eventRegistrationMachine/eventRegistrationContext';
import {getRouteApi, useNavigate} from '@tanstack/react-router';
import {Check, Clock, Loader2} from 'lucide-react';
import {FC, Fragment, ReactNode, useId, useMemo, useState} from 'react';
import {ActionRow} from './actionRow';
import {CategoryDropdown} from './categoryDropdown';
import {AnimatePresence, motion} from 'motion/react';
import {cn} from '@/lib/utils';
import {CategoryItem} from '@/components/eventRoutes/categories/categoryItem';
import {
  Category,
  CategoryPrice,
  EventRegistrationStatus,
} from '@/apiTypes/event';
import {createCheckoutSession} from '@/server/checkout';
import {formatPrice} from '@/utils/currencyUtils';

/**
 * Returns the currently active price for a category — the first price
 * whose endsAt is null (no expiry) or still in the future.
 */
function getActivePrice(
  prices: CategoryPrice[] | undefined
): CategoryPrice | undefined {
  if (!prices?.length) return undefined;
  const now = new Date();
  return prices.find((p) => p.endsAt === null || new Date(p.endsAt) > now);
}

const eventRoute = getRouteApi('/event/$eventId/');

/** Threshold at which the category picker switches from buttons to a dropdown. */
const CATEGORY_DROPDOWN_THRESHOLD = 3;

/**
 * Returns true when a category has an active non-zero price and the
 * organizer can accept payments — meaning registration should go
 * through the checkout flow.
 */
function isPaidCategory(
  category: Category | undefined,
  canAcceptPayments: boolean
): boolean {
  if (!category?.prices?.length || !canAcceptPayments) return false;
  const now = new Date();
  const activePrice = category.prices.find(
    (p) => p.endsAt === null || new Date(p.endsAt) > now
  );
  return activePrice != null && activePrice.amount > 0;
}

interface NativeRegistrationPanelProps {
  registrationStatus: EventRegistrationStatus;
  /** Rendered beside the primary action button (e.g. a Share button). */
  actionAccessory?: ReactNode;
}

/**
 * Renders the native registration UI driven by `eventRegistrationMachine`.
 *
 * Selection lives in the machine's context (`routeId`, `categoryId`) so
 * server submission, guards, and future features can rely on a single source
 * of truth. The panel just reads context and dispatches `SELECT_ROUTE` /
 * `SELECT_CATEGORY` on user interaction.
 *
 * - Single route, single category: just a Register button.
 * - Multiple routes: `<RouteDropdown>` above a category picker.
 * - ≤ 3 categories on the selected route: button list.
 * - > 3 categories on the selected route: `<CategoryDropdown>`.
 */
export function NativeRegistrationPanel({
  registrationStatus,
  actionAccessory,
}: NativeRegistrationPanelProps) {
  const {isRegistered} = registrationStatus;
  const state = useEventRegistrationState();
  const actor = useEventRegistrationActor()!;
  const {profile} = useAuth();
  const navigate = useNavigate();
  const {track} = useAnalytics();
  const routeLabelId = useId();
  const categoryLabelId = useId();
  const {
    context: {
      eventId,
      routeId: selectedRouteId,
      categoryId: selectedCategoryId,
    },
  } = state;
  const {event} = eventRoute.useLoaderData();

  const routes = event.routes ?? [];
  const allCategories = event.categories ?? [];
  const hasMultipleRoutes = routes.length > 1;

  const visibleCategories = useMemo(() => {
    if (selectedRouteId) {
      return allCategories.filter((c) => c.routeId === selectedRouteId);
    }
    return allCategories.filter((c) => !c.routeId);
  }, [allCategories, selectedRouteId]);

  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const activeRoute = routes.find((r) => r.id === selectedRouteId);
  const activeCategory = visibleCategories.find(
    (c) => c.id === selectedCategoryId
  );

  const handleSelectRoute = (routeId: string) => {
    // Auto-pick a category only when the new route has exactly one. If the
    // route has multiple categories, leave the category unselected so the
    // user must explicitly choose.
    const routeCategories = allCategories.filter((c) => c.routeId === routeId);
    const categoryId =
      routeCategories.length === 1 ? routeCategories[0].id : null;
    actor.send({type: 'SELECT_ROUTE', routeId, categoryId});
  };

  const handleSelectCategory = (categoryId: string) => {
    actor.send({type: 'SELECT_CATEGORY', categoryId});
  };

  const handleRegister = async () => {
    track('Registration Button Clicked', {
      event_id: event.id,
      event_name: event.name,
      intent: 'register',
      platform: null,
      authenticated: profile != null,
    });
    if (!profile) {
      // Encode the current selection in the redirect URL so the same choice
      // survives login — including when the user opens the magic-link email
      // in a different browser than they started in (common on mobile /
      // in-app browsers), which a sessionStorage handoff cannot.
      const pendingParams = new URLSearchParams();
      if (selectedRouteId) pendingParams.set('pendingRoute', selectedRouteId);
      if (selectedCategoryId)
        pendingParams.set('pendingCategory', selectedCategoryId);
      const query = pendingParams.toString();
      const redirect = query
        ? `/event/${eventId}?${query}`
        : `/event/${eventId}`;
      navigate({
        to: '/login',
        search: {redirect},
      });
      return;
    }

    // Paid category — go through checkout flow
    if (
      isPaidCategory(
        activeCategory,
        event.organizer?.canAcceptPayments ?? false
      )
    ) {
      setIsCheckoutLoading(true);
      try {
        const result = await createCheckoutSession({
          data: {
            eventId,
            categoryId: selectedCategoryId!,
          },
        });
        navigate({
          to: '/checkout/$registrationId',
          params: {registrationId: result.registrationId},
        });
      } catch (err) {
        setIsCheckoutLoading(false);
        console.error('Failed to create payment intent:', err);
      }
      return;
    }

    // Free category — existing machine flow
    actor.send({type: 'REGISTER'});
  };

  if (registrationStatus.registration?.status === 'PAID') {
    return (
      <ActionRow accessory={actionAccessory}>
        <Button
          disabled
          size="lg"
          className="w-full border border-success-300 text-success-800 font-medium bg-success-100 hover:bg-success-100"
        >
          <Check className="w-4 h-4 mr-2" />
          Registered
        </Button>
      </ActionRow>
    );
  }

  if (registrationStatus.registration?.status === 'DENIED') {
    return (
      <div className="flex flex-col gap-2">
        <ActionRow accessory={actionAccessory}>
          <Button
            disabled
            size="lg"
            className="w-full border border-error-300 text-error-800 font-medium bg-error-50 hover:bg-error-50"
          >
            Registration Not Approved
          </Button>
        </ActionRow>
        <Typography level="B4" className="text-error-700 text-center">
          Your registration was not approved. Please contact the organizer if
          you believe this is an error.
        </Typography>
      </div>
    );
  }

  if (
    registrationStatus.registration?.status === 'PENDING_PREREQUISITE_REVIEW'
  ) {
    return (
      <ActionRow accessory={actionAccessory}>
        <Button
          disabled
          size="lg"
          className="w-full border border-warning-300 text-warning-800 font-medium bg-warning-50 hover:bg-warning-50"
        >
          <Clock className="w-4 h-4 mr-2" />
          Pending Organizer Review
        </Button>
      </ActionRow>
    );
  }

  if (isRegistered || state.matches({flow: 'refreshing'})) {
    return (
      <ActionRow accessory={actionAccessory}>
        <Button
          disabled
          size="lg"
          className="w-full border border-success-300 text-success-800 font-medium bg-success-100 hover:bg-success-100"
        >
          <Check className="w-4 h-4 mr-2" />
          Registered
        </Button>
      </ActionRow>
    );
  }

  // if (isCheckoutLoading) {
  //   return (
  //     <Button disabled className="w-full" size="lg">
  //       <Loader2 className="w-4 h-4 mr-2 animate-spin" />
  //       Setting up checkout...
  //     </Button>
  //   );
  // }

  if (state.matches({flow: 'submitting'})) {
    return (
      <ActionRow accessory={actionAccessory}>
        <Button disabled className="w-full" size="lg">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Registering...
        </Button>
      </ActionRow>
    );
  }

  const useCategoryDropdown =
    visibleCategories.length > CATEGORY_DROPDOWN_THRESHOLD;
  const hasMultipleCategories = visibleCategories.length > 1;

  return (
    <div className="flex flex-col gap-4">
      {hasMultipleRoutes && (
        <div className="flex flex-col gap-1.5">
          <Typography level="B3" id={routeLabelId}>
            Route
          </Typography>
          <RouteDropdown
            routes={routes}
            activeRoute={activeRoute ?? null}
            onSelect={handleSelectRoute}
            aria-labelledby={routeLabelId}
          />
        </div>
      )}

      {hasMultipleCategories && (
        <div
          className="flex flex-col gap-1.5"
          role={useCategoryDropdown ? undefined : 'radiogroup'}
          aria-labelledby={useCategoryDropdown ? undefined : categoryLabelId}
        >
          <Typography
            level="C2"
            className="tracking-widest text-neutral-500"
            id={categoryLabelId}
          >
            CATEGORY
          </Typography>
          {useCategoryDropdown ? (
            <CategoryDropdown
              categories={visibleCategories}
              activeCategory={activeCategory ?? null}
              onSelect={handleSelectCategory}
              aria-labelledby={categoryLabelId}
            />
          ) : (
            <div className="flex flex-col gap-2">
              {visibleCategories.map((category, index) => (
                <Fragment key={category.id}>
                  <CategoryRadioButton
                    category={category}
                    active={selectedCategoryId === category.id}
                    disabled={!category.isOpen || category.isSoldOut}
                    onClick={() => handleSelectCategory(category.id)}
                  />
                  {index < visibleCategories.length - 1 && (
                    <div className="w-full border-b border-neutral-100" />
                  )}
                </Fragment>
              ))}
            </div>
          )}
        </div>
      )}

      {activeCategory &&
        (() => {
          const activePrice = getActivePrice(activeCategory.prices);
          if (!activePrice) return null;
          return (
            <div
              className={cn(
                'flex justify-between items-end',
                (hasMultipleRoutes || hasMultipleCategories) &&
                  'py-2 border-t border-neutral-100'
              )}
            >
              <Typography level="C2" className="text-neutral-500">
                TOTAL
              </Typography>
              <Typography level="S1" className="font-semibold italic">
                {activePrice.amount === 0
                  ? 'Free'
                  : formatPrice(activePrice.amount)}
              </Typography>
            </div>
          );
        })()}

      <ActionRow accessory={actionAccessory}>
        <Button
          className="w-full"
          size="lg"
          disabled={isCheckoutLoading || selectedCategoryId == null}
          onClick={handleRegister}
        >
          {isCheckoutLoading ? (
            <Loader2 className="animate-spin" />
          ) : (
            'Register'
          )}
        </Button>
      </ActionRow>
    </div>
  );
}

interface CategoryRadioButtonProps {
  active: boolean;
  category: Category;
  disabled: boolean;
  onClick: () => void;
}
const CategoryRadioButton: FC<CategoryRadioButtonProps> = ({
  category,
  onClick,
  active,
  disabled,
}) => {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'w-full rounded-sm flex gap-3 items-center p-3 transition-colors',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer hover:bg-neutral-50'
      )}
    >
      <div className="flex gap-3 items-center">
        {/* Radio Dot */}
        <div
          className={cn(
            'h-4 w-4 border rounded-full p-0.5 transition-border',
            active ? 'border-primary-500' : 'border-neutral-400'
          )}
        >
          <AnimatePresence>
            {active && (
              <motion.div
                className="w-full h-full bg-primary-500 rounded-full"
                initial={{scale: 0}}
                animate={{
                  scale: 1,
                  opacity: 1,
                }}
                exit={{
                  scale: 0,
                  opacity: 0,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 25,
                }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
      <CategoryItem category={category} />
    </button>
  );
};
